/**
 * Settings Page
 *
 * Epic 9: System Settings & Master Data Management.
 * Toegankelijk voor rol `admin` en `hr` (Story TD-2, tech-debt-followup --
 * voorheen strikt admin-only op routeniveau). Een subset van de tabs blijft
 * wél strikt admin-only via het per-item `roles`-veld hieronder, 1:1
 * gebaseerd op de bestaande backend-autorisatie per tab.
 * Uses collapsible menu groups for better organization.
 */

import { useState } from 'react';
import type { Role } from '@functieloonhuis/shared';
import { useAuth } from '../auth';
import { Layout } from '../../components/Layout';
import { RolesTab } from './components/RolesTab';
import { DepartmentsTab } from './components/DepartmentsTab';
import { FunctionsTab } from './components/FunctionsTab';
import { RoleTypesTab } from './components/RoleTypesTab';
import { CoreTasksTab } from './components/CoreTasksTab';
import { CoreTaskMigrationTab } from './components/CoreTaskMigrationTab';
import { TasksSettingsTab } from './components/TasksSettingsTab';
import { CompetenciesSettingsTab } from './components/CompetenciesSettingsTab';
import { TaskCompetenciesTab } from './components/TaskCompetenciesTab';
import { EvaluationCyclesTab } from './components/EvaluationCyclesTab';
import { SalaryRangesTab } from './components/SalaryRangesTab';
import { NiveauRangesTab } from './components/NiveauRangesTab';
import { ScoreCriteriaTab } from './components/ScoreCriteriaTab';
import { CompetencyMatrixTab } from './components/CompetencyMatrixTab';
import { AgreementTypesTab } from './components/AgreementTypesTab';
import { SystemSettingsTab } from './components/SystemSettingsTab';
import { HolidaysTab } from './components/HolidaysTab';
import { SelfServiceTab } from './components/SelfServiceTab';
import { AuditLogTab } from './components/AuditLogTab';
import { ImportExportTab } from './components/ImportExportTab';
import { BackupsTab } from './components/BackupsTab';
import { EmployeeSyncTab } from './components/EmployeeSyncTab';
import { ArchivedAgreementsTab } from './components/ArchivedAgreementsTab';

type TabId = 'roles' | 'departments' | 'functions' | 'roleTypes' | 'coreTasks' | 'coreTaskMigration' | 'tasks' | 'competencies' | 'taskCompetencies' | 'competencyMatrix' | 'cycles' | 'salaries' | 'niveauRanges' | 'scoreCriteria' | 'agreementTypes' | 'holidays' | 'archivedAgreements' | 'selfService' | 'importExport' | 'auditLog' | 'system' | 'backups' | 'employeeSync';

type GroupId = 'organisatie' | 'functieloonhuis' | 'beoordeling' | 'beloning' | 'afspraken' | 'toegang' | 'systeem';

interface MenuItem {
  id: TabId;
  name: string;
  icon: React.ReactNode;
  /**
   * Story TD-2 (tech-debt-followup): optionele rolrestrictie bovenop de
   * route-guard (die sinds deze story ['admin', 'hr'] toestaat). Ontbreekt
   * dit veld, dan is het item zichtbaar voor iedereen die de route al mag
   * bezoeken (admin + hr). De 7 items die dit veld WEL zetten
   * (`roles: ['admin']`) zijn 1:1 gebaseerd op bestaande backend-`requireRole`
   * -restricties (zie de story voor de regelverwijzingen per tab) -- dit is
   * dus geen nieuwe autorisatiegrens, alleen het gelijktrekken van de
   * frontend-zichtbaarheid met wat de backend al afdwingt.
   */
  roles?: Role[];
}

interface MenuGroup {
  id: GroupId;
  name: string;
  icon: React.ReactNode;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    id: 'organisatie',
    name: 'Organisatie',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    items: [
      {
        id: 'departments',
        name: 'Teams',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
      },
      {
        id: 'functions',
        name: 'Functies',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        id: 'roles',
        name: 'Rollen & Rechten',
        // Backend: settings.ts GET /settings/roles is requireRole('admin') (regel 73) -- strikt admin-only.
        roles: ['admin'],
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'functieloonhuis',
    name: 'Functieloonhuis',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m4-11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    items: [
      {
        id: 'coreTasks',
        name: 'Kerntaken',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        ),
      },
      {
        id: 'roleTypes',
        name: 'Roltypes',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
      },
      {
        // Epic 6 — Migratie & livegang (Story 6.3). Tijdelijk item (verdwijnt
        // of wordt read-only archief na livegang, zie de tab-component's
        // header-comment) -- bewust in de bestaande "Functieloonhuis"-groep
        // i.p.v. een nieuwe groep (verifieerd bij implementatie: deze groep
        // bestond al uit Epic 1/2).
        id: 'coreTaskMigration',
        name: 'Migratie kerntaken',
        // Backend: coreTasks.ts /migration* is requireRole('admin') (regel 51 e.v.) -- strikt admin-only,
        // ook expliciet zo gedocumenteerd in CoreTaskMigrationTab.tsx's eigen header-comment.
        roles: ['admin'],
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'beoordeling',
    name: 'Beoordeling',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    items: [
      {
        id: 'cycles',
        name: 'Beoordelingscycli',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        id: 'scoreCriteria',
        name: 'Score Criteria',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
      },
      {
        id: 'tasks',
        name: 'Taken',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        ),
      },
      {
        id: 'competencies',
        name: 'Competenties',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        ),
      },
      {
        id: 'taskCompetencies',
        name: 'Taken-Competenties',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        ),
      },
      {
        id: 'competencyMatrix',
        name: 'Scoringsmatrix',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'beloning',
    name: 'Beloning',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    items: [
      {
        id: 'salaries',
        name: 'Salarisschalen',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
      },
      {
        id: 'niveauRanges',
        name: 'Niveau Punten',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'afspraken',
    name: 'Afspraken',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    items: [
      {
        id: 'agreementTypes',
        name: 'Afspraaktypes',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        ),
      },
      {
        id: 'holidays',
        name: 'Feestdagen',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        ),
      },
      {
        id: 'archivedAgreements',
        name: 'Verwijderde Afspraken',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'toegang',
    name: 'Toegang & Privacy',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
    items: [
      {
        id: 'selfService',
        name: 'Self-Service',
        // Backend: settings.ts GET /self-service is requireRole('admin') (regel 1464) -- strikt admin-only.
        roles: ['admin'],
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      },
      {
        id: 'auditLog',
        name: 'Audit Log',
        // Backend: settings.ts GET /audit-log is requireRole('admin') (regel 1542) -- strikt admin-only.
        roles: ['admin'],
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'systeem',
    name: 'Systeem',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    items: [
      {
        id: 'system',
        name: 'Systeem Instellingen',
        // Backend: settings.ts GET /system is requireRole('admin') (regel 1240) -- strikt admin-only.
        roles: ['admin'],
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        ),
      },
      {
        id: 'importExport',
        name: 'Import/Export',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
        ),
      },
      {
        id: 'backups',
        name: 'Database Backups',
        // Backend: backups.ts is requireRole('admin') op alle endpoints -- strikt admin-only.
        roles: ['admin'],
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
        ),
      },
      {
        id: 'employeeSync',
        name: 'Medewerker Sync',
        // EmployeeSyncTab.tsx's eigen header-comment: "Admin-only tab for
        // synchronizing employees with MySQL users" -- beheert account-
        // koppelingen, bewust strikt admin-only (ondanks dat de onderliggende
        // sync.ts GET-endpoints technisch ook hr toestaan).
        roles: ['admin'],
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ),
      },
    ],
  },
];

// Helper to find which group contains the active tab
function findGroupForTab(tabId: TabId): GroupId {
  for (const group of menuGroups) {
    if (group.items.some(item => item.id === tabId)) {
      return group.id;
    }
  }
  return 'organisatie';
}

/**
 * Story TD-2 (tech-debt-followup): is `tabId` toegestaan voor de huidige
 * gebruiker? Ontbreekt `roles` op het item, dan is het toegestaan voor
 * iedereen die de `/instellingen`-route al mag bezoeken (admin + hr). Een
 * onbekende `tabId` wordt standaard geweigerd (fail-closed).
 *
 * Los geëxporteerd (i.p.v. alleen inline gebruikt) zodat dit ook direct
 * unit-testbaar is als defense-in-depth-check, onafhankelijk van of de
 * sidebar die tab daadwerkelijk aanbiedt -- zie AC4 in de story.
 */
export function isTabAllowedForRole(tabId: TabId, hasRole: (roles: Role | Role[]) => boolean): boolean {
  for (const group of menuGroups) {
    const item = group.items.find(i => i.id === tabId);
    if (item) {
      return !item.roles || hasRole(item.roles);
    }
  }
  return false;
}

export function SettingsPage() {
  const { hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('departments');
  const [expandedGroups, setExpandedGroups] = useState<Set<GroupId>>(() => {
    // Initially expand the group containing the active tab
    return new Set([findGroupForTab('departments')]);
  });

  // Story TD-2: filter de sidebar tot de items/groepen die de huidige rol mag
  // zien. Een groep zonder overgebleven items (bv. "Toegang & Privacy" voor
  // hr, waar zowel Self-Service als Audit Log admin-only zijn) wordt zelf ook
  // niet getoond -- geen lege, uitklapbare groep zonder inhoud.
  const visibleMenuGroups = menuGroups
    .map(group => ({ ...group, items: group.items.filter(item => !item.roles || hasRole(item.roles)) }))
    .filter(group => group.items.length > 0);

  const toggleGroup = (groupId: GroupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleTabClick = (tabId: TabId) => {
    setActiveTab(tabId);
    // Ensure the group is expanded when clicking a tab
    const groupId = findGroupForTab(tabId);
    setExpandedGroups(prev => {
      if (prev.has(groupId)) return prev;
      return new Set([...prev, groupId]);
    });
  };

  const renderTabContent = () => {
    // Story TD-2, AC4 -- defense-in-depth: de sidebar biedt een admin-only
    // tab sowieso niet aan voor hr, maar mocht `activeTab` ooit toch een niet-
    // toegestane waarde bevatten (devtools, een toekomstige deep-link), dan
    // rendert hier geen tab-component maar een duidelijke weigering. De
    // backend-endpoints blijven de echte autorisatiegrens.
    if (!isTabAllowedForRole(activeTab, hasRole)) {
      return (
        <div data-testid="settings-tab-forbidden" className="p-6 text-sm text-gray-500">
          Geen toegang tot dit onderdeel.
        </div>
      );
    }
    switch (activeTab) {
      case 'roles':
        return <RolesTab />;
      case 'departments':
        return <DepartmentsTab />;
      case 'functions':
        return <FunctionsTab />;
      case 'roleTypes':
        return <RoleTypesTab />;
      case 'coreTasks':
        return <CoreTasksTab />;
      case 'coreTaskMigration':
        return <CoreTaskMigrationTab />;
      case 'tasks':
        return <TasksSettingsTab />;
      case 'competencies':
        return <CompetenciesSettingsTab />;
      case 'taskCompetencies':
        return <TaskCompetenciesTab />;
      case 'competencyMatrix':
        return <CompetencyMatrixTab />;
      case 'cycles':
        return <EvaluationCyclesTab />;
      case 'salaries':
        return <SalaryRangesTab />;
      case 'niveauRanges':
        return <NiveauRangesTab />;
      case 'scoreCriteria':
        return <ScoreCriteriaTab />;
      case 'agreementTypes':
        return <AgreementTypesTab />;
      case 'holidays':
        return <HolidaysTab />;
      case 'archivedAgreements':
        return <ArchivedAgreementsTab />;
      case 'selfService':
        return <SelfServiceTab />;
      case 'importExport':
        return <ImportExportTab />;
      case 'auditLog':
        return <AuditLogTab />;
      case 'system':
        return <SystemSettingsTab />;
      case 'backups':
        return <BackupsTab />;
      case 'employeeSync':
        return <EmployeeSyncTab />;
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div data-testid="settings-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Systeeminstellingen</h1>
          <p className="mt-1 text-sm text-gray-500">
            Beheer systeemconfiguratie, rollen, teams en andere master data
          </p>
        </div>

        {/* Main content with sidebar */}
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <nav className="w-64 flex-shrink-0">
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
              {visibleMenuGroups.map((group) => (
                <div key={group.id} className="border-b border-gray-200 last:border-b-0">
                  {/* Group Header */}
                  <button
                    data-testid={`settings-group-${group.id}`}
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{group.icon}</span>
                      <span className="font-medium text-gray-900">{group.name}</span>
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                        expandedGroups.has(group.id) ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Group Items */}
                  {expandedGroups.has(group.id) && (
                    <div className="bg-gray-50 py-1">
                      {group.items.map((item) => (
                        <button
                          key={item.id}
                          data-testid={`settings-tab-${item.id}`}
                          onClick={() => handleTabClick(item.id)}
                          className={`w-full flex items-center gap-3 px-4 py-2 pl-12 text-left text-sm transition-colors ${
                            activeTab === item.id
                              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                        >
                          <span className={activeTab === item.id ? 'text-blue-600' : 'text-gray-400'}>
                            {item.icon}
                          </span>
                          <span>{item.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </nav>

          {/* Tab Content */}
          <div className="flex-1 min-w-0">
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
